import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useToast } from '../../common/Toast';
import UserSelect from '../../common/UserSelect';
import { ROLE_COLORS } from './RoleCard';
import './RoleItemPanel.scss';

const RoleItemPanel = ({
  deliverableId,
  deliverableTitle,
  roleType,
  assigneeId,
  items,
  usersMap,
  companyUsers,
  onClose,
  onToggleCompleted,
  onItemTitleBlur,
  onItemHoursBlur,
  onItemActualHoursBlur,
  onItemNotesBlur,
  onDeleteItem,
  onAddItem,
  onAssigneeChange,
  onRenameRole,
  existingRoles = [],
}) => {
  const toast = useToast();
  const [newItemTitle, setNewItemTitle] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(roleType);
  const nameInputRef = useRef(null);
  const newItemRef = useRef(null);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleAddItem = () => {
    if (!newItemTitle.trim()) return;
    onAddItem(deliverableId, roleType, assigneeId, newItemTitle.trim());
    setNewItemTitle('');
    setTimeout(() => newItemRef.current?.focus(), 0);
  };

  const assignee = assigneeId ? usersMap[assigneeId] : null;
  const badgeColor = ROLE_COLORS[roleType] || '#667eea';

  // Stats
  const totalCount = items.length;
  const doneCount = items.filter((i) => i.is_completed).length;
  const totalEstHours = items.reduce((s, i) => s + (parseFloat(i.estimated_hours) || 0), 0);
  const totalActualHours = items.reduce((s, i) => s + (parseFloat(i.actual_hours) || 0), 0);

  const panel = (
    <>
      <div className="role-panel-overlay" onClick={onClose} />
      <div className="role-panel">
        {/* Header */}
        <div className="role-panel-header">
          <button className="role-panel-close" onClick={onClose} title="關閉">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4l10 10M14 4L4 14" strokeLinecap="round" />
            </svg>
          </button>
          <div className="role-panel-title-area">
            {editingName ? (
              <input
                ref={nameInputRef}
                className="role-panel-name-input"
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onBlur={() => {
                  setEditingName(false);
                  const trimmed = nameValue.trim();
                  if (!trimmed || trimmed === roleType) return;
                  if (existingRoles.includes(trimmed)) {
                    toast.error(`角色「${trimmed}」已存在`);
                    setNameValue(roleType);
                    return;
                  }
                  onRenameRole(deliverableId, roleType, trimmed);
                }}
                onKeyDown={(e) => {
                  if (e.nativeEvent.isComposing) return;
                  if (e.key === 'Enter') e.target.blur();
                  if (e.key === 'Escape') { setEditingName(false); setNameValue(roleType); }
                }}
              />
            ) : (
              <span
                className="role-panel-badge"
                style={{ backgroundColor: badgeColor, cursor: 'pointer' }}
                onClick={() => {
                  setNameValue(roleType);
                  setEditingName(true);
                  setTimeout(() => nameInputRef.current?.focus(), 0);
                }}
                title="點擊編輯角色名稱"
              >
                {roleType}
              </span>
            )}
            <span className="role-panel-deliverable">{deliverableTitle}</span>
          </div>
          <div className="role-panel-assignee">
            <UserSelect
              values={assigneeId ? [assigneeId] : []}
              onChange={(ids) => onAssigneeChange(deliverableId, roleType, ids[0] || null)}
              users={companyUsers}
              singleSelect
              placeholder="指派人..."
            />
          </div>
        </div>

        {/* Add item - pinned at top */}
        <div className="role-panel-add">
          <input
            ref={newItemRef}
            className="role-panel-add-input"
            placeholder="新增工作項目..."
            value={newItemTitle}
            onChange={(e) => setNewItemTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.nativeEvent.isComposing) return;
              if (e.key === 'Enter') handleAddItem();
              if (e.key === 'Escape') setNewItemTitle('');
            }}
          />
          <button
            className="role-panel-add-btn"
            onClick={handleAddItem}
            disabled={!newItemTitle.trim()}
          >
            新增
          </button>
        </div>

        {/* Item list */}
        <div className="role-panel-body">
          {items.length === 0 ? (
            <div className="role-panel-empty">
              <p>尚無工作項目</p>
              <p className="sub">在上方輸入框新增第一個項目</p>
            </div>
          ) : (
            items.map((item) => (
              <PanelItemRow
                key={item.id}
                item={item}
                onToggle={() => onToggleCompleted(item)}
                onTitleBlur={(v) => onItemTitleBlur(item, v)}
                onHoursBlur={(v) => onItemHoursBlur(item, v)}
                onActualHoursBlur={(v) => onItemActualHoursBlur(item, v)}
                onNotesBlur={(v) => onItemNotesBlur(item, v)}
                onDelete={() => onDeleteItem(item.id)}
              />
            ))
          )}
        </div>

        {/* Footer summary */}
        {totalCount > 0 && (
          <div className="role-panel-footer">
            <span>{doneCount}/{totalCount} 完成</span>
            {totalEstHours > 0 && <span>預估 {totalEstHours}h</span>}
            {totalActualHours > 0 && <span className="actual">實際 {totalActualHours}h</span>}
          </div>
        )}
      </div>
    </>
  );

  return ReactDOM.createPortal(panel, document.body);
};

// ─── Panel Item Row ─────────────────

const PanelItemRow = ({ item, onToggle, onTitleBlur, onHoursBlur, onActualHoursBlur, onNotesBlur, onDelete }) => {
  const [title, setTitle] = useState(item.title);
  const [hours, setHours] = useState(item.estimated_hours ?? '');
  const [actualHours, setActualHours] = useState(item.actual_hours ?? '');
  const [notes, setNotes] = useState(item.notes || '');
  const [completed, setCompleted] = useState(item.is_completed);

  useEffect(() => { setCompleted(item.is_completed); }, [item.is_completed]);
  useEffect(() => { setTitle(item.title); }, [item.title]);
  useEffect(() => { setHours(item.estimated_hours ?? ''); }, [item.estimated_hours]);
  useEffect(() => { setActualHours(item.actual_hours ?? ''); }, [item.actual_hours]);

  const handleToggle = () => {
    setCompleted(!completed);
    onToggle();
  };

  return (
    <div className={`rp-item ${completed ? 'completed' : ''}`}>
      <div className="rp-item-main">
        <button className="rp-item-check" onClick={handleToggle}>
          {completed ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="1" y="1" width="14" height="14" rx="3" fill="#10b981" stroke="#10b981" strokeWidth="1.5" />
              <path d="M4.5 8l2.5 2.5 4.5-4.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="1" y="1" width="14" height="14" rx="3" stroke="#ccc" strokeWidth="1.5" />
            </svg>
          )}
        </button>

        <input
          className="rp-item-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => onTitleBlur(title)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) e.target.blur(); }}
        />

        <button className="rp-item-delete" onClick={onDelete} title="刪除">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M2 2l8 8M10 2l-8 8" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <div className="rp-item-fields">
        <div className="rp-item-field">
          <label>預估</label>
          <input
            type="number"
            min="0"
            step="0.5"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            onBlur={() => onHoursBlur(hours)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) e.target.blur(); }}
            placeholder="0"
          />
          <span className="unit">h</span>
        </div>

        <div className="rp-item-field actual">
          <label>實際</label>
          <input
            type="number"
            min="0"
            step="0.5"
            value={actualHours}
            onChange={(e) => setActualHours(e.target.value)}
            onBlur={() => onActualHoursBlur(actualHours)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) e.target.blur(); }}
            placeholder="0"
          />
          <span className="unit">h</span>
        </div>

      </div>

      <div className="rp-item-notes">
        <input
          className="rp-item-notes-input"
          placeholder="備註..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => onNotesBlur(notes)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) e.target.blur(); }}
        />
      </div>
    </div>
  );
};

export default RoleItemPanel;
